import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Bell,
  BellOff,
  BellRing,
  Check,
  X,
  Loader2
} from 'lucide-react';
import notificationService from '../services/notificationService';
import { useToast } from '../hooks/use-toast';

const NotificationBell = ({ 
  webinar = null, 
  webinarUrl = null,
  showLabel = false,
  variant = 'default' // 'default' | 'outline' | 'ghost'
}) => {
  const [permission, setPermission] = useState('default');
  const [isEnabled, setIsEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [scheduledCount, setScheduledCount] = useState(0);
  const { toast } = useToast();

  useEffect(() => {
    updatePermissionStatus();
    // Check localStorage for user preference
    const savedPreference = localStorage.getItem(`notification-webinar-${webinar?.id}`);
    if (savedPreference === 'enabled') {
      setIsEnabled(true);
    }
  }, [webinar?.id]);

  const updatePermissionStatus = () => {
    const status = notificationService.getPermissionStatus();
    setPermission(status);
    setScheduledCount(notificationService.getScheduledCount());
  };

  const handleEnableNotifications = async () => {
    setLoading(true);
    try {
      const granted = await notificationService.requestPermission();
      updatePermissionStatus();

      if (granted) {
        if (webinar && webinarUrl) {
          // Schedule reminders for this specific webinar
          const scheduled = notificationService.scheduleWebinarReminders(webinar, webinarUrl);
          setScheduledCount(notificationService.getScheduledCount());
          
          if (scheduled.length > 0) {
            localStorage.setItem(`notification-webinar-${webinar.id}`, 'enabled');
            setIsEnabled(true);
            toast({
              title: "🔔 Notificaciones activadas",
              description: `Recibirás ${scheduled.length} recordatorios antes del webinar`,
            });
          }
        } else {
          toast({
            title: "🔔 Notificaciones activadas",
            description: "Recibirás recordatorios de tus webinars",
          });
        }
      } else {
        toast({
          title: "Notificaciones bloqueadas",
          description: "Activa las notificaciones en la configuración de tu navegador",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
      toast({
        title: "Error",
        description: "No se pudieron activar las notificaciones",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDisableNotifications = () => {
    if (webinar) {
      // Cancel notifications for this webinar
      notificationService.cancelScheduledNotification(`webinar-${webinar.id}-1day`);
      notificationService.cancelScheduledNotification(`webinar-${webinar.id}-1hour`);
      notificationService.cancelScheduledNotification(`webinar-${webinar.id}-15min`);
      notificationService.cancelScheduledNotification(`webinar-${webinar.id}-start`);
      localStorage.removeItem(`notification-webinar-${webinar.id}`);
    } else {
      notificationService.cancelAllScheduledNotifications();
    }
    setIsEnabled(false);
    setScheduledCount(notificationService.getScheduledCount());
    toast({
      title: "Notificaciones desactivadas",
      description: webinar ? "No recibirás recordatorios para este webinar" : "Se cancelaron todos los recordatorios",
    });
  };

  // Don't render if notifications are not supported
  if (permission === 'unsupported') {
    return null;
  }

  // Render based on current state
  if (permission === 'denied') {
    return (
      <Button
        variant="outline"
        size="sm"
        disabled
        className="text-gray-400"
      >
        <BellOff className="h-4 w-4 mr-2" />
        {showLabel && 'Notificaciones bloqueadas'}
      </Button>
    );
  }

  if (isEnabled && webinar) {
    return (
      <Button
        variant={variant}
        size="sm"
        onClick={handleDisableNotifications}
        className="text-green-600 hover:text-green-700"
      >
        <BellRing className="h-4 w-4 mr-2" />
        {showLabel ? 'Recordatorio activo' : <Check className="h-3 w-3" />}
      </Button>
    );
  }

  return (
    <Button
      variant={variant}
      size="sm"
      onClick={handleEnableNotifications}
      disabled={loading}
      className={variant === 'default' ? 'bg-gray-900 hover:bg-gray-800' : ''}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <>
          <Bell className="h-4 w-4 mr-2" />
          {showLabel && 'Activar recordatorio'}
        </>
      )}
    </Button>
  );
};

export default NotificationBell;
